import React, { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '../theme';

// ============================================================================
// SPINNER VARIANTS
// ============================================================================

const spinnerVariants = cva(
  'animate-spin',
  {
    variants: {
      size: {
        xs: 'h-3 w-3',
        sm: 'h-4 w-4',
        md: 'h-6 w-6',
        lg: 'h-8 w-8',
        xl: 'h-12 w-12',
      },
      variant: {
        default: 'text-sage',
        primary: 'text-sage',
        secondary: 'text-navy-light',
        muted: 'text-warm-gray',
        white: 'text-white',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  }
);

// ============================================================================
// SPINNER
// ============================================================================

export interface SpinnerProps
  extends HTMLAttributes<SVGSVGElement>,
    VariantProps<typeof spinnerVariants> {
  /**
   * Accessible label for the spinner.
   */
  label?: string;
}

/**
 * Loading spinner component.
 * 
 * @example
 * // Basic spinner
 * <Spinner />
 * 
 * // Large spinner with label
 * <Spinner size="lg" label="Loading content..." />
 * 
 * // Inside a button
 * <Button disabled>
 *   <Spinner size="sm" variant="white" />
 *   Loading...
 * </Button>
 */
function Spinner({ className, size, variant, label, ...props }: SpinnerProps) {
  return (
    <Loader2
      className={cn(spinnerVariants({ size, variant }), className)}
      aria-label={label || 'Loading'}
      role="status"
      {...props}
    />
  );
}

// ============================================================================
// LOADING OVERLAY
// ============================================================================

interface LoadingOverlayProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Text to display below the spinner.
   */
  message?: string;
  /**
   * Spinner size.
   */
  spinnerSize?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Full-screen or container loading overlay.
 */
function LoadingOverlay({
  className,
  message = 'Loading...',
  spinnerSize = 'lg',
  ...props
}: LoadingOverlayProps) {
  return (
    <div
      className={cn(
        'absolute inset-0 flex flex-col items-center justify-center',
        'bg-white/80 backdrop-blur-sm z-50',
        className
      )}
      {...props}
    >
      <Spinner size={spinnerSize} />
      {message && (
        <p className="mt-3 text-sm text-warm-gray">{message}</p>
      )}
    </div>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { Spinner, LoadingOverlay, spinnerVariants };
